export const COLUMN_LAYOUT_EVENT = 'bbm-column-layout';
export const COLUMN_MIN_PX = 40;
export const COLUMN_RESIZE_HANDLE_PX = 10;
export const COLUMN_HEADER_GAP_PX = 8;
export const COLUMN_USER_WIDTH_ATTR = 'data-col-user-width';
export const COLUMN_HIDDEN_CLASS = 'col-layout-hidden';
export const COLUMN_AUTOSIZE_MAX_PX = 720;

export interface IColumnPickerItem {
  key: string;
  label: string;
  visible: boolean;
  locked: boolean;
}

/** Intrinsic width of header content that must stay visible (label, not the current cell box). */
export function measureUntruncatedWidth(el: HTMLElement | null): number {
  if (!el) {
    return 0;
  }
  const computed = getComputedStyle(el);
  const clone = el.cloneNode(true) as HTMLElement;
  clone.style.position = 'absolute';
  clone.style.left = '-99999px';
  clone.style.top = '0';
  clone.style.visibility = 'hidden';
  clone.style.pointerEvents = 'none';
  clone.style.display = 'inline-block';
  clone.style.width = 'auto';
  clone.style.height = 'auto';
  clone.style.minWidth = '0';
  clone.style.maxWidth = 'none';
  clone.style.overflow = 'visible';
  clone.style.whiteSpace = 'nowrap';
  clone.style.flex = 'none';
  clone.classList.remove('cell-ellipsis', COLUMN_HIDDEN_CLASS);
  clone.style.font = computed.font;
  clone.style.fontSize = computed.fontSize;
  clone.style.fontFamily = computed.fontFamily;
  clone.style.fontWeight = computed.fontWeight;
  clone.style.letterSpacing = computed.letterSpacing;
  clone.style.padding = computed.padding;
  document.body.appendChild(clone);
  const width = Math.ceil(Math.max(clone.getBoundingClientRect().width, clone.scrollWidth, 0));
  clone.remove();
  return width;
}

function measurePlainHeaderText(th: HTMLElement): number {
  const text = Array.from(th.childNodes)
    .filter(node => node.nodeType === Node.TEXT_NODE)
    .map(node => (node.textContent || '').trim())
    .filter(Boolean)
    .join(' ');
  if (!text) {
    return 0;
  }
  const probe = document.createElement('span');
  probe.textContent = text;
  probe.style.position = 'absolute';
  probe.style.left = '-99999px';
  probe.style.whiteSpace = 'nowrap';
  const computed = getComputedStyle(th);
  probe.style.font = computed.font;
  probe.style.fontSize = computed.fontSize;
  probe.style.fontFamily = computed.fontFamily;
  probe.style.fontWeight = computed.fontWeight;
  document.body.appendChild(probe);
  const width = Math.ceil(probe.getBoundingClientRect().width);
  probe.remove();
  return width;
}

/**
 * Drag floor. Header text and sort/filter icons do not raise it, so a column
 * can be narrower than its heading. The handle stays grabbable at this width.
 */
export function measureColumnHeaderMinWidth(_th: HTMLElement): number {
  return COLUMN_MIN_PX;
}

/** Untruncated header label plus sort/filter icons. Used to fit a column, not as the drag floor. */
export function measureColumnHeaderContentWidth(th: HTMLElement): number {
  const styles = getComputedStyle(th);
  const paddingX = (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0);
  const title = th.querySelector('.ant-table-column-title') as HTMLElement | null;
  const sorter = th.querySelector('.ant-table-column-sorter') as HTMLElement | null;
  const filter = th.querySelector('.ant-table-filter-trigger') as HTMLElement | null;
  const sorters = th.querySelector('.ant-table-column-sorters') as HTMLElement | null;
  const gapStyle = sorters ? getComputedStyle(sorters) : null;
  const gap = gapStyle
    ? parseFloat(gapStyle.columnGap || gapStyle.gap) || COLUMN_HEADER_GAP_PX
    : COLUMN_HEADER_GAP_PX;
  const titleWidth = title
    ? measureUntruncatedWidth(title)
    : measurePlainHeaderText(th);
  const sorterWidth = sorter ? Math.max(12, measureUntruncatedWidth(sorter)) : 0;
  const filterWidth = filter ? measureUntruncatedWidth(filter) : 0;
  const gaps = (sorterWidth > 0 ? gap : 0) + (filterWidth > 0 ? gap : 0);
  return Math.ceil(paddingX + titleWidth + sorterWidth + filterWidth + gaps + COLUMN_RESIZE_HANDLE_PX);
}

/** Content width of a body cell, ignoring ellipsis / max-width:0 from table-layout. */
export function measureTableCellContentWidth(td: HTMLElement): number {
  const styles = getComputedStyle(td);
  const paddingX = (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0);
  const probe = document.createElement('div');
  probe.style.position = 'absolute';
  probe.style.left = '-99999px';
  probe.style.top = '0';
  probe.style.visibility = 'hidden';
  probe.style.pointerEvents = 'none';
  probe.style.display = 'inline-block';
  probe.style.width = 'auto';
  probe.style.maxWidth = 'none';
  probe.style.minWidth = '0';
  probe.style.overflow = 'visible';
  probe.style.whiteSpace = 'nowrap';
  probe.style.font = styles.font;
  probe.style.fontSize = styles.fontSize;
  probe.style.fontFamily = styles.fontFamily;
  probe.style.fontWeight = styles.fontWeight;
  probe.style.letterSpacing = styles.letterSpacing;
  if (td.childNodes.length) {
    Array.from(td.childNodes).forEach(node => probe.appendChild(node.cloneNode(true)));
  } else {
    probe.textContent = (td.textContent || '').replace(/\s+/g, ' ').trim();
  }
  probe.querySelectorAll('*').forEach(node => {
    const el = node as HTMLElement;
    el.style.maxWidth = 'none';
    el.style.width = 'auto';
    el.style.overflow = 'visible';
    el.style.whiteSpace = 'nowrap';
    el.classList.remove('cell-ellipsis', COLUMN_HIDDEN_CLASS);
  });
  document.body.appendChild(probe);
  const inner = Math.ceil(Math.max(probe.getBoundingClientRect().width, probe.scrollWidth, 0));
  probe.remove();
  return inner + Math.ceil(paddingX);
}

/** Fit a column to header + visible cell content, capped so one column cannot eat the table. */
export function measureColumnAutosizeWidth(root: HTMLElement, key: string): number {
  const selector = `[data-col-key="${CSS.escape(key)}"]`;
  const ths = Array.from(root.querySelectorAll(`th${selector}`)) as HTMLTableCellElement[];
  const th = ths.find(cell => cell.getBoundingClientRect().height > 0) || ths[0] || null;
  const headerContent = th ? measureColumnHeaderContentWidth(th) : COLUMN_MIN_PX;
  let max = Math.max(COLUMN_MIN_PX, headerContent);
  const tds = Array.from(root.querySelectorAll(`td${selector}`)) as HTMLElement[];
  if (!tds.length && th && th.cellIndex >= 0) {
    const index = th.cellIndex;
    root.querySelectorAll<HTMLTableRowElement>('.ant-table-tbody tr').forEach(row => {
      const td = row.children[index] as HTMLElement | undefined;
      if (td) {
        tds.push(td);
      }
    });
  }
  tds.forEach(td => {
    if (td.classList.contains(COLUMN_HIDDEN_CLASS) || td.closest('tr.ant-table-measure-now')) {
      return;
    }
    max = Math.max(max, measureTableCellContentWidth(td));
  });
  return Math.min(COLUMN_AUTOSIZE_MAX_PX, Math.max(COLUMN_MIN_PX, max));
}

/** Grow to content, and if the table is narrower than the panel, give this column the leftover. */
export function fillColumnWidth(
  contentWidth: number,
  othersWidth: number,
  viewportWidth: number,
): number {
  const fill = Math.max(0, Math.round(viewportWidth - othersWidth));
  return Math.max(COLUMN_MIN_PX, contentWidth, fill);
}

export interface IColumnLayout {
  order: string[];
  widths: Record<string, number>;
  hidden?: string[];
}

const POSITIONAL_COLUMN_KEY = /^(?:col|fixed)-\d+$/;

export function isPositionalColumnKey(key: string): boolean {
  return POSITIONAL_COLUMN_KEY.test(key);
}

export function parseColumnLayout(raw: unknown): IColumnLayout | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const source = raw as { order?: unknown; widths?: unknown; hidden?: unknown };
  const order = Array.isArray(source.order)
    ? source.order.filter((key): key is string => typeof key === 'string' && key.length > 0 && !isPositionalColumnKey(key))
    : [];
  const widths: Record<string, number> = {};
  if (source.widths && typeof source.widths === 'object' && !Array.isArray(source.widths)) {
    Object.entries(source.widths as Record<string, unknown>).forEach(([key, value]) => {
      if (isPositionalColumnKey(key)) {
        return;
      }
      if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
        widths[key] = value;
      }
    });
  }
  const hidden = Array.isArray(source.hidden)
    ? source.hidden.filter((key): key is string => typeof key === 'string' && key.length > 0 && !isPositionalColumnKey(key))
    : [];
  return { order, widths, hidden };
}

export function mergeColumnLayout(
  currentKeys: string[],
  stored: IColumnLayout | null,
  defaultWidths: Record<string, number>,
): IColumnLayout {
  const currentSet = new Set(currentKeys);
  const seen = new Set<string>();
  const order: string[] = [];
  (stored?.order || []).forEach(key => {
    if (currentSet.has(key) && !seen.has(key)) {
      seen.add(key);
      order.push(key);
    }
  });
  currentKeys.forEach((key, index) => {
    if (seen.has(key)) {
      return;
    }
    let insertAt = 0;
    for (let i = index - 1; i >= 0; i--) {
      const neighbor = order.indexOf(currentKeys[i]);
      if (neighbor >= 0) {
        insertAt = neighbor + 1;
        break;
      }
    }
    order.splice(insertAt, 0, key);
    seen.add(key);
  });
  const widths: Record<string, number> = {};
  currentKeys.forEach(key => {
    const storedWidth = stored?.widths?.[key];
    const fallback = defaultWidths[key] ?? COLUMN_MIN_PX;
    widths[key] = typeof storedWidth === 'number' && storedWidth > 0
      ? Math.max(COLUMN_MIN_PX, storedWidth)
      : fallback;
  });
  return {
    order,
    widths,
    hidden: sanitizeHidden(currentKeys, stored?.hidden || []),
  };
}

/** Drop unknown/locked keys and refuse to hide every hideable column. */
export function sanitizeHidden(
  currentKeys: string[],
  hidden: string[],
  lockedKeys: string[] = [],
): string[] {
  const current = new Set(currentKeys);
  const locked = new Set(lockedKeys);
  const hideable = currentKeys.filter(key => !locked.has(key));
  const next: string[] = [];
  const seen = new Set<string>();
  hidden.forEach(key => {
    if (!current.has(key) || locked.has(key) || seen.has(key)) {
      return;
    }
    seen.add(key);
    next.push(key);
  });
  if (!hideable.length || next.length >= hideable.length) {
    return [];
  }
  return next;
}

export function columnLayoutKey(tableId: string): string {
  return `table.columns.${tableId}`;
}

/** Move `dragged` before/after `target` without dropping unknown keys. */
export function placeColumnKey(
  order: string[],
  dragged: string,
  target: string,
  after: boolean,
): string[] {
  if (!dragged || !target || dragged === target) {
    return order.slice();
  }
  const from = order.indexOf(dragged);
  const to = order.indexOf(target);
  if (from < 0 || to < 0) {
    return order.slice();
  }
  const next = order.filter(key => key !== dragged);
  let insertAt = next.indexOf(target);
  if (insertAt < 0) {
    return order.slice();
  }
  if (after) {
    insertAt += 1;
  }
  next.splice(insertAt, 0, dragged);
  return next;
}

/** Keep fixed (non-movable) keys in place; replace movable slots with `nextMovable`. */
export function mergeMovableOrder(
  fullOrder: string[],
  movableKeys: string[],
  nextMovable: string[],
): string[] {
  const movable = new Set(movableKeys);
  let index = 0;
  return fullOrder.map(key => {
    if (!movable.has(key)) {
      return key;
    }
    return nextMovable[index++] ?? key;
  });
}
